import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import Modal from "@/components/Modal";
import { Field } from "@/pages/Income";
import {
  Users, Plus, Copy, Share2, LogOut, Trash2, Trophy, Flame, Sparkles,
  Crown, ArrowLeft, ArrowRight, CalendarClock, UserPlus, Shield
} from "lucide-react";

export default function Circles() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [circles, setCircles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", description: "" });
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/circles");
      setCircles(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openDetail = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/circles/${id}`);
      setSelected(data);
    } catch { toast.error(t("toasts.circle_load_failed")); }
  }, []);

  // Auto-open circle if URL has ?circle=<id>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cid = params.get("circle");
    if (cid && circles.some(c => c.id === cid)) openDetail(cid);
  }, [circles, openDetail]);

  // Auto-open join modal if landing captured a join code
  useEffect(() => {
    try {
      const pending = localStorage.getItem("pfo_join");
      if (pending) {
        setJoinCode(pending);
        setJoinOpen(true);
        localStorage.removeItem("pfo_join");
      }
    } catch (err) {
      console.warn("Circles: could not read pending join code from localStorage", err);
    }
  }, []);

  const createCircle = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/circles", createForm);
      toast.success(`"${data.name}" created`);
      setCreateOpen(false); setCreateForm({ name: "", description: "" });
      await loadList();
      openDetail(data.id);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const joinCircle = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const { data } = await api.post("/circles/join", { invite_code: joinCode });
      toast.success(data.already_member ? `You're already in "${data.name}"` : `Joined "${data.name}"`);
      setJoinOpen(false); setJoinCode("");
      await loadList();
      openDetail(data.id);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to join"); }
    finally { setBusy(false); }
  };

  const leaveCircle = async (circle) => {
    if (!confirm(`Leave "${circle.name}"? You'll lose access to the leaderboard.`)) return;
    try {
      const { data } = await api.delete(`/circles/${circle.id}/leave`);
      if (data?.deleted) {
        toast.success(`Left "${circle.name}" — the circle closed with you.`);
      } else if (data?.transferred_to) {
        toast.success(t("toasts.left_ownership_passed"));
      } else {
        toast.success(t("toasts.left_circle"));
      }
      setSelected(null); loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteCircle = async (circle) => {
    if (!confirm(`Delete "${circle.name}"? This removes it for everyone.`)) return;
    try {
      await api.delete(`/circles/${circle.id}`);
      toast.success(t("toasts.circle_deleted"));
      setSelected(null); loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="text-sm text-[var(--text-muted)]">{t("pages.circles.loading")}</div>;

  if (selected) return (
    <CircleDetail
      circle={selected}
      currentUserId={user?.user_id}
      onBack={() => setSelected(null)}
      onLeave={() => leaveCircle(selected)}
      onDelete={() => deleteCircle(selected)}
      onRefresh={() => openDetail(selected.id)}
    />
  );

  return (
    <div>
      <PageHeader
        subtitle={t("pages.circles.subtitle")}
        title={t("pages.circles.title")}
        action={
          <div className="flex gap-2 flex-wrap">
            <button className="btn-ghost" onClick={() => setJoinOpen(true)} data-testid="join-circle-btn">
              <Users size={16}/> {t("pages.circles.join_with_code")}
            </button>
            <button className="btn-primary" onClick={() => setCreateOpen(true)} data-testid="create-circle-btn">
              <Plus size={16}/> {t("pages.circles.create_circle")}
            </button>
          </div>
        }
      />

      <div className="card-flat p-6 mb-6" style={{
        background: "linear-gradient(120deg, rgba(45,90,64,0.06) 0%, rgba(224,158,80,0.04) 100%)",
      }}>
        <div className="grid md:grid-cols-3 gap-6">
          <PillarCard icon={<Trophy size={18}/>} title={t("pages.circles.pillar1_t")} body={t("pages.circles.pillar1_b")}/>
          <PillarCard icon={<Flame size={18}/>}  title={t("pages.circles.pillar2_t")} body={t("pages.circles.pillar2_b")}/>
          <PillarCard icon={<Sparkles size={18}/>} title={t("pages.circles.pillar3_t")} body={t("pages.circles.pillar3_b")}/>
        </div>
      </div>

      {circles.length === 0 ? (
        <EmptyState
          title={t("pages.circles.empty_title")}
          body={t("pages.circles.empty_full")}
          action={
            <div className="flex gap-2 flex-wrap justify-center">
              <button className="btn-primary" onClick={() => setCreateOpen(true)}><Plus size={16}/> {t("pages.circles.create_first")}</button>
              <button className="btn-ghost" onClick={() => setJoinOpen(true)}><Users size={16}/> {t("pages.circles.join_with_code")}</button>
            </div>
          }
        />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
          {circles.map(c => (
            <button key={c.id} onClick={() => openDetail(c.id)}
              className="card-flat p-5 text-left hover:-translate-y-0.5 transition-transform"
              data-testid={`circle-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-lg font-medium" style={{fontFamily:'var(--font-heading)'}}>{c.name}</div>
                  {c.description && <div className="text-sm text-[var(--text-secondary)] mt-1 line-clamp-2">{c.description}</div>}
                </div>
                {c.is_owner && <span className="badge badge-success">{t("pages.circles.owner")}</span>}
              </div>
              <div className="mt-5 flex items-center justify-between text-sm text-[var(--text-secondary)]">
                <div className="flex items-center gap-1.5">
                  <Users size={14}/> {t("pages.circles.members_of_max", { count: c.member_count, max: c.max_members })}
                </div>
                <div className="font-mono text-xs text-[var(--text-muted)]">{c.invite_code}</div>
              </div>
              <div className="mt-4 text-xs text-[var(--accent-primary)] flex items-center gap-1">
                {t("pages.circles.open_leaderboard")} <ArrowRight size={13}/>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Create modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title={t("pages.circles.create_title")}>
        <form onSubmit={createCircle} className="grid gap-4">
          <Field label={t("pages.circles.circle_name_l")}><input required className="field-input" value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} placeholder={t("pages.circles.circle_name_p")} data-testid="input-circle-name" autoFocus/></Field>
          <Field label={t("pages.circles.desc_l")}><input className="field-input" value={createForm.description} onChange={e => setCreateForm({...createForm, description: e.target.value})} placeholder={t("pages.circles.desc_p")} data-testid="input-circle-desc"/></Field>
          <p className="text-xs text-[var(--text-muted)]">{t("pages.circles.invite_hint")}</p>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setCreateOpen(false)}>{t("common.cancel")}</button>
            <button type="submit" className="btn-primary" disabled={busy} data-testid="submit-create-circle">{busy ? t("pages.circles.creating") : t("pages.circles.create_circle")}</button>
          </div>
        </form>
      </Modal>

      {/* Join modal */}
      <Modal open={joinOpen} onClose={() => setJoinOpen(false)} title={t("pages.circles.join_title")}>
        <form onSubmit={joinCircle} className="grid gap-4">
          <Field label={t("pages.circles.code_l")}>
            <input
              required
              className="field-input font-mono uppercase tracking-widest"
              value={joinCode}
              onChange={e => setJoinCode(e.target.value.toUpperCase().slice(0, 12))}
              placeholder={t("pages.circles.code_p")}
              data-testid="input-join-code"
              autoFocus
              style={{ letterSpacing: "0.2em" }}
            />
          </Field>
          <p className="text-xs text-[var(--text-muted)]">{t("pages.circles.code_hint")}</p>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setJoinOpen(false)}>{t("common.cancel")}</button>
            <button type="submit" className="btn-primary" disabled={busy || joinCode.length < 4} data-testid="submit-join-circle">{busy ? t("pages.circles.joining") : t("pages.circles.join")}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function PillarCard({ icon, title, body }) {
  return (
    <div>
      <div className="h-9 w-9 rounded-lg bg-white border border-[var(--border-default)] flex items-center justify-center text-[var(--accent-primary)]">
        {icon}
      </div>
      <div className="mt-3 font-medium text-sm" style={{fontFamily:'var(--font-heading)'}}>{title}</div>
      <div className="mt-1 text-sm text-[var(--text-secondary)] leading-relaxed">{body}</div>
    </div>
  );
}

function WeeklyDigest({ digest, loading }) {
  if (loading) {
    return (
      <div className="card-flat p-6 mb-8" data-testid="digest-loading">
        <div className="label-eyebrow">Weekly digest</div>
        <div className="mt-2 text-sm text-[var(--text-muted)]">Composing the week&apos;s highlights…</div>
      </div>
    );
  }
  if (!digest) return null;

  const eventIcon = (t) => {
    if (t === "save_used") return <Shield size={14} color="#86A8D8"/>;
    if (t === "joined") return <UserPlus size={14} color="#437A55"/>;
    return <Sparkles size={14}/>;
  };

  return (
    <div className="card-flat p-6 mb-8 relative overflow-hidden animate-fadeup" data-testid="circle-digest" style={{
      background: "linear-gradient(155deg, #21422F 0%, #2D5A40 55%, #437A55 100%)",
      color: "#FDFCFB",
      borderColor: "transparent",
    }}>
      <div className="absolute -top-16 -right-10 h-56 w-56 rounded-full pointer-events-none"
           style={{ background: "radial-gradient(circle, rgba(224,158,80,0.30) 0%, rgba(224,158,80,0) 70%)" }}/>
      <div className="relative">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2" style={{ opacity: 0.85 }}>
            <CalendarClock size={16} color="#E09E50"/>
            <div className="label-eyebrow" style={{ color: "#FDFCFB", opacity: 0.85 }}>Weekly digest</div>
          </div>
          <div className="text-xs font-mono" style={{ opacity: 0.7 }}>
            {digest.week_start} → {digest.week_end}
          </div>
        </div>

        <h3 className="mt-3 text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "var(--font-heading)", fontWeight: 500, letterSpacing: "-0.02em", lineHeight: 1.15 }} data-testid="digest-headline">
          {digest.ai_headline}
        </h3>

        {/* Aggregate stats row */}
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          <DigestStat label="Members"     value={digest.member_count}/>
          <DigestStat label="Avg streak"  value={`${digest.avg_streak} mo`}/>
          <DigestStat label="Avg score"   value={digest.avg_health}/>
          <DigestStat label="Saves used"  value={digest.total_saves_used_this_week} tone={digest.total_saves_used_this_week > 0 ? "warm" : "neutral"}/>
        </div>

        {/* Events + near milestones */}
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="rounded-xl p-4" style={{ background: "rgba(0,0,0,0.18)" }}>
            <div className="text-[10px] tracking-[0.22em] uppercase mb-3" style={{ opacity: 0.7 }}>This week</div>
            {digest.events.length === 0 ? (
              <div className="text-sm" style={{ opacity: 0.75 }}>Quiet week — no big moves. Steady discipline.</div>
            ) : (
              <ul className="space-y-2.5">
                {digest.events.slice(0, 6).map((e) => (
                  <li key={`${e.user_id}-${e.type}-${e.detail || ""}`} className="flex items-start gap-3 text-sm" data-testid={`digest-event-${e.type}-${e.user_id}`}>
                    <div className="mt-1 flex-shrink-0">{eventIcon(e.type)}</div>
                    <div>
                      <span className="font-medium">{e.name}</span>{" "}
                      <span style={{ opacity: 0.85 }}>{e.detail}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl p-4" style={{ background: "rgba(0,0,0,0.18)" }}>
            <div className="text-[10px] tracking-[0.22em] uppercase mb-3" style={{ opacity: 0.7 }}>Close to next milestone</div>
            {digest.near_milestones.length === 0 ? (
              <div className="text-sm" style={{ opacity: 0.75 }}>Nobody within 2 months of the next tier — long game.</div>
            ) : (
              <ul className="space-y-2.5">
                {digest.near_milestones.map((m) => (
                  <li key={`${m.user_id}-${m.milestone}`} className="flex items-start gap-3 text-sm" data-testid={`digest-milestone-${m.user_id}`}>
                    <Flame size={14} color="#E09E50" className="mt-1 flex-shrink-0"/>
                    <div>
                      <span className="font-medium">{m.name}</span>{" "}
                      <span style={{ opacity: 0.85 }}>is <span className="font-mono">{m.months_away}</span> month{m.months_away === 1 ? "" : "s"} from <span style={{ color: "#E09E50", fontWeight: 600 }}>{m.milestone}</span></span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {digest.top_member && (
          <div className="mt-5 flex items-center gap-3 rounded-xl p-3 pl-4" style={{ background: "rgba(224,158,80,0.14)", border: "1px solid rgba(224,158,80,0.35)" }}>
            <Crown size={18} color="#E09E50"/>
            <div className="text-sm">
              <span className="font-medium">{digest.top_member.name}</span>{" "}
              <span style={{ opacity: 0.85 }}>leads with a </span>
              <span className="font-mono font-medium">{digest.top_member.current_streak}</span>-month streak
              <span style={{ opacity: 0.85 }}> · health <span className="font-mono">{digest.top_member.health_score}</span></span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DigestStat({ label, value, tone = "neutral" }) {
  const color = tone === "warm" ? "#E09E50" : "#FDFCFB";
  return (
    <div className="rounded-lg p-3" style={{ background: "rgba(0,0,0,0.12)" }}>
      <div className="text-[10px] tracking-[0.2em] uppercase" style={{ opacity: 0.7 }}>{label}</div>
      <div className="font-mono font-medium text-2xl mt-1" style={{ color, letterSpacing: "-0.02em", lineHeight: 1 }}>{value}</div>
    </div>
  );
}

function CircleDetail({ circle, currentUserId, onBack, onLeave, onDelete, onRefresh }) {
  const { t } = useTranslation();
  const inviteLink = `${window.location.origin}/?ref=${currentUserId}&join=${circle.invite_code}`;
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setDigestLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/circles/${circle.id}/digest`);
        if (!cancelled) setDigest(data);
      } catch (err) {
        console.warn(`Circles: failed to load digest for ${circle.id}`, err);
      } finally {
        if (!cancelled) setDigestLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [circle.id]);

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(circle.invite_code); toast.success("Code copied"); } catch { toast.error(t("common.toast_copy_failed")); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(inviteLink); toast.success("Invite link copied"); } catch { toast.error(t("common.toast_copy_failed")); }
  };
  const shareLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Join "${circle.name}" on PFO`,
          text: `Join our Money Circle "${circle.name}" — private accountability, no amounts shared. Use code ${circle.invite_code}:`,
          url: inviteLink,
        });
      } else { copyLink(); }
    } catch (err) {
      // Includes AbortError when the user cancels the share sheet — that
      // path is intentional, so we log at debug level rather than warn.
      if (err?.name !== "AbortError") console.warn("Circle share failed", err);
    }
  };

  const top = circle.members?.[0];
  const you = circle.members?.find(m => m.user_id === currentUserId);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <button className="btn-ghost" onClick={onBack} data-testid="circle-back-btn"><ArrowLeft size={15}/> All circles</button>
        <div className="flex gap-2 flex-wrap">
          <button className="btn-ghost" onClick={onRefresh} data-testid="circle-refresh-btn">Refresh</button>
          <button className="btn-danger-ghost" onClick={onLeave} data-testid="circle-leave-btn"><LogOut size={15}/> Leave</button>
          {circle.is_owner && (
            <button className="btn-danger-ghost" onClick={onDelete} data-testid="circle-delete-btn"><Trash2 size={15}/> Delete circle</button>
          )}
        </div>
      </div>

      <div className="mb-8 animate-fadeup">
        <div className="label-eyebrow mb-2">Money Circle {circle.is_owner && "· You own this"}</div>
        <h1 className="text-3xl md:text-4xl tracking-tighter font-medium" style={{fontFamily:'var(--font-heading)'}} data-testid="circle-detail-name">{circle.name}</h1>
        {circle.description && <p className="text-[var(--text-secondary)] mt-2">{circle.description}</p>}
      </div>

      {/* Invite panel */}
      <div className="card-flat p-6 mb-8 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <div className="label-eyebrow">Invite code</div>
          <div className="flex items-center gap-3 mt-2">
            <div className="font-mono text-3xl font-medium tracking-[0.3em] text-[var(--accent-primary)]" data-testid="circle-invite-code">
              {circle.invite_code}
            </div>
            <button className="btn-ghost" onClick={copyCode} data-testid="copy-code-btn"><Copy size={14}/></button>
          </div>
          <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-[var(--bg-accent)] border border-[var(--border-default)]">
            <input readOnly value={inviteLink} className="flex-1 bg-transparent text-xs font-mono text-[var(--text-secondary)] outline-none min-w-0 truncate" data-testid="circle-invite-link"/>
            <button className="btn-ghost" onClick={copyLink}><Copy size={14}/></button>
            <button className="btn-primary" onClick={shareLink} data-testid="circle-share-btn"><Share2 size={14}/> Share</button>
          </div>
        </div>
        <div className="flex items-center md:justify-end">
          <div className="text-center">
            <div className="num-hero" data-testid="circle-member-count">{circle.member_count}</div>
            <div className="label-eyebrow mt-1">/ {circle.max_members} members</div>
          </div>
        </div>
      </div>

      {/* Weekly Digest */}
      <WeeklyDigest digest={digest} loading={digestLoading}/>

      {/* Leaderboard */}
      <div className="mb-2 flex items-end justify-between">
        <div>
          <div className="label-eyebrow mb-2">Leaderboard</div>
          <h2 className="text-2xl tracking-tighter" style={{fontFamily:'var(--font-heading)'}}>Ranked by streak, then health</h2>
        </div>
        <div className="text-xs text-[var(--text-muted)]">No amounts are ever shared</div>
      </div>

      <div className="card-flat overflow-hidden mt-4">
        {circle.members?.map((m, idx) => {
          const isYou = m.user_id === currentUserId;
          const isTop = idx === 0 && (m.current_streak > 0 || m.health_score > 0);
          return (
            <div key={m.user_id}
                 className={`px-5 py-4 flex items-center gap-4 border-b border-[var(--border-default)] last:border-0 ${isYou ? "bg-[var(--bg-accent)]/40" : ""}`}
                 data-testid={`circle-member-${m.user_id}`}>
              <div className="w-10 text-center">
                {isTop
                  ? <Crown size={22} className="text-[#E09E50] inline"/>
                  : <span className="font-mono text-lg text-[var(--text-muted)]">#{m.rank}</span>}
              </div>
              {m.picture
                ? <img src={m.picture} alt="" className="h-11 w-11 rounded-full object-cover border border-[var(--border-default)]"/>
                : <div className="h-11 w-11 rounded-full bg-[var(--bg-accent)] flex items-center justify-center font-semibold text-[var(--accent-primary)]">{m.name?.[0]?.toUpperCase()}</div>}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {m.name} {isYou && <span className="text-[10px] uppercase tracking-widest text-[var(--accent-primary)] ml-1">You</span>}
                </div>
                <div className="text-xs text-[var(--text-muted)] mt-0.5">
                  {m.wins_last_12mo}/12 winning months · {m.saves_used_year} save{m.saves_used_year === 1 ? "" : "s"} used
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 justify-end">
                  <Flame size={14} color="#E09E50"/>
                  <div className="num-md">{m.current_streak}</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">streak</div>
              </div>
              <div className="text-right w-16">
                <div className={`num-md ${m.health_score >= 70 ? "text-[var(--accent-positive)]" : m.health_score >= 40 ? "text-[#A16A25]" : "text-[var(--accent-danger)]"}`}>
                  {m.health_score}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">score</div>
              </div>
            </div>
          );
        })}
      </div>

      {you && (
        <div className="mt-6 card-flat p-5 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-[var(--bg-accent)] flex items-center justify-center text-[var(--accent-primary)]">
            <Sparkles size={18}/>
          </div>
          <div className="flex-1">
            <div className="font-medium">
              {top && top.user_id !== currentUserId
                ? `You're #${you.rank}. Catch ${top.name} — they're ${top.current_streak - you.current_streak} month${top.current_streak - you.current_streak === 1 ? "" : "s"} ahead.`
                : circle.member_count > 1
                  ? "You're leading the circle. Keep it going."
                  : "Invite a friend to start the accountability."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
